import { useState, useEffect } from 'react'
import { GetStaticProps } from 'next'
import { useAudioStore } from '../../components/TranscriptEditor/useAudioStore'
import { TranscriptContainer } from '../../components/TranscriptEditor/TranscriptContainer'
import { PromptsList } from 'components/Marketing/PromptsList'
import { MarketingHero } from 'components/Marketing/MarketingHero'
import { usePrompts } from 'hooks/prompts'
import { useContentPackType } from 'hooks/content_pack_types'
import { TranscriptHeader } from 'components/TranscriptEditor/TranscriptHeader'
import { SubmissionPrompt } from 'lib/types/generated-types'
import { MarketingHeading } from 'components/Marketing/MarketingHeading'
import Head from 'next/head'
import { supabase } from 'lib/supabase-server'
import { SectionTitle } from 'components/Marketing/SectionTitle'
import { VideoSection } from 'components/Marketing/VideoSection'
import { ContentDisplay } from 'components/Marketing/ContentDisplay'
import { MarketingFooter } from 'components/Marketing/MarketingFooter'

interface TranscriptData {
  submission: any
  transcript: any
  content: SubmissionPrompt[]
}

export default function Podcasters({
  data,
  content,
}: {
  data: TranscriptData
  content: SubmissionPrompt[]
}) {
  const { setAudioUrl, cleanupAudio } = useAudioStore()

  useEffect(() => {
    async function fetchAudioUrl() {
      const audioKey = data.submission?.s3_audio_key
      if (audioKey) {
        try {
          const response = await fetch(
            `/api/get-audio-url?audioKey=${encodeURIComponent(audioKey)}`
          )
          const { signedUrl } = await response.json()
          setAudioUrl(signedUrl)
        } catch (error) {
          console.error('Failed to fetch audio URL:', error)
        }
      }
    }

    fetchAudioUrl()
    return () => {
      cleanupAudio()
    }
  }, [data.submission])

  const { data: prompts } = usePrompts({
    contentPackTypeId: data.submission.content_pack_type_id,
  })

  const { data: contentPackType } = useContentPackType({
    typeId: data.submission.content_pack_type_id,
  })

  const mappedPromptsToContent: (SubmissionPrompt & { name: string })[] =
    prompts?.map((prompt) => {
      const foundContent = content.find((c) => c.prompt_id === prompt.id)
      if (!foundContent) {
        return {
          id: prompt.id,
          prompt_id: prompt.id,
          prompt_text: prompt.prompt_text,
          ai_response: null,
          created_at: null,
          created_by: null,
          error_text: null,
          generation_cost: null,
          status: null,
          submission_id: '',
          updated_at: null,
          name: prompt.name,
        }
      }
      return { ...foundContent, name: prompt.name }
    }) || []

  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768)
    }
    handleResize()
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  if (!data?.transcript || !data?.submission || !contentPackType) {
    return null
  }

  return (
    <>
      <Head>
        <title>{contentPackType.name} Use Case | Outcast AI</title>
        <meta name="description" content={contentPackType.description || ''} />
        <meta
          property="og:title"
          content={`${contentPackType.name} Use Case | Outcast AI`}
        />
        <meta
          property="og:description"
          content={contentPackType.description || ''}
        />
        <meta
          name="twitter:title"
          content={`${contentPackType.name} Use Case | Outcast AI`}
        />
        <meta
          name="twitter:description"
          content={contentPackType.description || ''}
        />
      </Head>
      <div className="mx-auto mb-8 flex h-full w-full max-w-7xl flex-col gap-4 px-2 sm:px-6">
        <MarketingHeading />

        {/* Mobile MarketingHero */}
        <div className="md:hidden">
          <MarketingHero
            title={contentPackType.name}
            description={contentPackType.description}
          />
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <div className="order-2 flex h-fit w-full flex-col gap-4 md:order-1">
            {/* Desktop MarketingHero */}
            <div className="hidden md:block">
              <MarketingHero
                title={contentPackType.name}
                description={contentPackType.description}
              />
            </div>
            <div className="hidden h-full w-fit gap-2 rounded-lg border border-slate-200 p-2 md:flex">
              <PromptsList
                prompts={prompts || []}
                contentPackType={contentPackType}
                onPromptClick={(promptId) => {
                  const element = document.getElementById(
                    `prompt-content-${promptId}`
                  )
                  if (element) {
                    element.scrollIntoView({ behavior: 'smooth' })
                  }
                }}
              />
            </div>
            <div className="flex flex-col gap-4 pb-4 sm:pt-4">
              <SectionTitle title="Transcript">
                <TranscriptContainer
                  submission={data.submission}
                  transcript={data.transcript}
                />
              </SectionTitle>
            </div>
          </div>
          <div className="order-1 mb-8 flex h-full w-full flex-col justify-start gap-4 md:order-2">
            <VideoSection transcriptData={data} />
            <SectionTitle title="Generated Content">
              <ContentDisplay content={mappedPromptsToContent} />
            </SectionTitle>
          </div>
        </div>
        <div className="sticky bottom-2 z-10 mx-2 rounded-lg border border-slate-200 bg-white shadow-lg">
          <TranscriptHeader
            submission={data.submission}
            transcript={data.transcript}
            hasExportOptions={!isMobile}
          />
        </div>
      </div>
      <MarketingFooter />
    </>
  )
}

const formatContentPackName = (name: string) => {
  return name.toLowerCase().replace(/\s+/g, '-')
}

const submissionIds = [
  // /podcaster
  '0225b359-c537-47bb-8334-4a0f95e194fa',
  // /youtuber
  '509c355a-4a29-4cea-91f0-5ee12ed70efd',
  // /keynote
  '7d2d2fba-20b6-4687-b7b0-1743487af636',
  // /meeting
  '8b451f2e-5629-4b3d-a388-8375cd462acb',
  // /interview
  'ffc6d3e7-0752-4bc1-8a7b-68475b74b679',
  // /coaching-session
  'e91d3f9f-93f4-43aa-9b79-b5d35d558ab6',
]

export const getStaticPaths = async () => {
  // Hard‐coded IDs we want pages for:

  const paths: { params: { content_pack_name: string } }[] = []

  // For each ID, fetch that submission from your Supabase DB
  for (const submissionId of submissionIds) {
    const { data: submission, error } = await supabase
      .from('submissions')
      .select('*, content_pack_type:content_pack_type_id(name)')
      .eq('id', submissionId)
      .single()

    if (error || !submission?.content_pack_type) {
      continue
    }

    // Convert the content pack name (e.g. "My Pack") into "my-pack"
    const contentPackNameSlug = formatContentPackName(
      submission.content_pack_type.name || ''
    )

    // Push that into our "paths" array
    paths.push({
      params: {
        content_pack_name: contentPackNameSlug,
      },
    })
  }

  // This ensures Next.js *only* builds pages for your static list of IDs
  return {
    paths,
    fallback: false,
  }
}

export const getStaticProps: GetStaticProps = async ({ params }) => {
  // The slug, e.g. "my-pack"
  const contentPackName = params?.content_pack_name as string

  // 1. Convert it back to the real name
  //    e.g. "my-pack" -> "my pack"
  const maybeName = contentPackName.replace(/-/g, ' ')

  // 2. Find the content pack type by that name
  const { data: contentPackType } = await supabase
    .from('content_pack_types')
    .select('id, name')
    .ilike('name', maybeName)
    .single()

  if (!contentPackType) {
    return { notFound: true }
  }

  // 4. Grab the single submission that matches this pack type *and* is in our list
  const { data: submission } = await supabase
    .from('submissions')
    .select('*, content_pack_type:content_pack_type_id(name)')
    .eq('content_pack_type_id', contentPackType.id)
    .in('id', submissionIds) // ensure we only pick from your static list
    .single()

  if (!submission) {
    return { notFound: true }
  }

  // 5. Grab your prompts for that single submission
  const { data: prompts } = await supabase
    .from('submission_prompts')
    .select('*')
    .eq('submission_id', submission.id)

  return {
    props: {
      data: {
        submission: {
          ...submission,
          transcript: null,
        },
        transcript: submission.transcript,
      },
      content: prompts ?? [],
    },
    revalidate: 60 * 60 * 2,
  }
}
